import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  Check,
  Clock,
  Copy,
  Flame,
  FolderUp,
  KeyRound,
  Lock,
  MessageSquare,
  QrCode,
  RotateCcw,
  Send,
  Share2,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
  Zap,
} from "lucide-react";
import { TIERS, smartRetentionSeconds, type ProductSkuId } from "@limeshare/shared";
import { api } from "../api";
import { CryptoCheckoutModal, getSavedPassCode } from "../components/CryptoCheckoutModal";
import { FileIcon } from "../components/FileIcon";
import { HeroStoryLeft } from "../components/HeroStoryLeft";
import { HomeShowcase } from "../components/HomeSections";
import { canOptimizeImage, optimizeImageInBrowser } from "../engine/optimize";
import { listResumable, resumeProgress, type ResumeRecord } from "../engine/resume-store";
import { useUi } from "../i18n";
import { SpeedTracker, type TransferStats } from "../engine/speed";
import {
  discardResume,
  matchFilesForResume,
  resumeTransfer,
  uploadTransfer,
  validateSelection,
  type UploadProgress,
  type UploadResult,
} from "../engine/upload";
import { loadSent, markDestroyed, pruneSent, removeSent, type SentTransfer } from "../history";
import { getTurnstileToken } from "../turnstile";
import { releaseWakeLock, requestWakeLock } from "../engine/wakelock";
import { errorMessage, formatBytes, formatDuration } from "../util";

type Stage = { kind: "idle" } | { kind: "busy"; progress: UploadProgress | null; stats: TransferStats | null } | { kind: "done"; result: UploadResult };

const tier = TIERS.guest;
const MAX_RETENTION = tier.retentionSeconds ?? 30 * 86_400;
const EXPIRY_OPTIONS = [
  { label: "1 hour", seconds: 3600 },
  { label: "6 hours", seconds: 6 * 3600 },
  { label: "24 hours", seconds: 24 * 3600 },
  { label: "72 hours (3 days)", seconds: 72 * 3600 },
  { label: "7 days", seconds: 7 * 86_400 },
  { label: "30 days (Free ≤100 MB)", seconds: 30 * 86_400 },
  { label: "1 Year (Pass / Extend)", seconds: 365 * 86_400 },
];

export function Home() {
  const [files, setFiles] = useState<File[]>([]);
  const [burn, setBurn] = useState(false);
  const [retention, setRetention] = useState(72 * 3600);
  const [message, setMessage] = useState("");
  const [senderBrand, setSenderBrand] = useState("");
  const [paywallUsd, setPaywallUsd] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passCode, setPassCode] = useState("");
  const [checkoutSku, setCheckoutSku] = useState<ProductSkuId | null>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [stage, setStage] = useState<Stage>({ kind: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const speedTrackerRef = useRef(new SpeedTracker());

  useEffect(() => {
    const urlPass = new URLSearchParams(window.location.search).get("pass");
    const saved = urlPass || getSavedPassCode();
    if (saved) setPassCode(saved);
  }, []);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    setError(null);
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of Array.from(incoming)) {
        if (!merged.some((x) => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified)) merged.push(f);
      }
      const next = merged.slice(0, 2500);
      const nextTotal = next.reduce((s, f) => s + f.size, 0);
      setRetention(smartRetentionSeconds(nextTotal));
      return next;
    });
  }, []);

  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);

  const triggerConfetti = () => {
    try {
      confetti({
        particleCount: 70,
        spread: 60,
        origin: { y: 0.65 },
        colors: ["#00ff66", "#7cff5c", "#ffffff", "#5ce1ff"],
      });
    } catch {
      /* best effort visual */
    }
  };

  const start = async () => {
    const totalBytes = files.reduce((s, f) => s + f.size, 0);
    if (!passCode && (totalBytes > tier.maxTransferBytes || files.length > tier.maxFiles)) {
      setCheckoutSku(totalBytes > 200 * 1024 ** 3 ? "exceed-1tb" : totalBytes > 50 * 1024 ** 3 ? "exceed-200g" : "exceed-50g");
      return;
    }
    const problem = validateSelection(files, Boolean(passCode));
    if (problem) return setError(problem);
    if (usePassword && password.length < 8) return setError("Password must be at least 8 characters.");
    setError(null);
    speedTrackerRef.current.start();
    void requestWakeLock();
    setStage({ kind: "busy", progress: null, stats: null });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const turnstileToken = turnstileRef.current ? await getTurnstileToken(turnstileRef.current) : undefined;
      const parsedPaywall = Number(paywallUsd);
      const result = await uploadTransfer({
        files,
        burn,
        retentionSeconds: retention,
        ...(message.trim() ? { message } : {}),
        ...(senderBrand.trim() ? { sender: senderBrand.trim() } : {}),
        ...(parsedPaywall >= 2 ? { paywallPriceUsd: parsedPaywall } : {}),
        ...(usePassword ? { password } : {}),
        ...(passCode.trim() ? { boostCode: passCode.trim() } : {}),
        ...(turnstileToken ? { turnstileToken } : {}),
        signal: controller.signal,
        onProgress: (p) => {
          const stats = speedTrackerRef.current.update(p.bytesDone, p.bytesTotal);
          setStage({ kind: "busy", progress: p, stats });
        },
      });
      triggerConfetti();
      setStage({ kind: "done", result });
    } catch (e) {
      setStage({ kind: "idle" });
      setError((e as Error)?.name === "AbortError" ? "Upload cancelled." : errorMessage(e));
    } finally {
      abortRef.current = null;
      void releaseWakeLock();
    }
  };

  const reset = () => {
    setFiles([]);
    setBurn(false);
    setMessage("");
    setPassword("");
    setUsePassword(false);
    setStage({ kind: "idle" });
    setError(null);
  };

  const resume = async (record: ResumeRecord, picked: File[]) => {
    const match = matchFilesForResume(record, picked);
    const missing = record.files.filter((_, i) => match.files[i] === null).map((f) => f.name);
    if (missing.length) return setError(`Still missing: ${missing.join(", ")}. Select the exact same file(s).`);
    setError(null);
    speedTrackerRef.current.start();
    void requestWakeLock();
    setStage({ kind: "busy", progress: null, stats: null });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await resumeTransfer(
        match,
        (p) => {
          const stats = speedTrackerRef.current.update(p.bytesDone, p.bytesTotal);
          setStage({ kind: "busy", progress: p, stats });
        },
        controller.signal,
      );
      setBurn(record.burn);
      triggerConfetti();
      setStage({ kind: "done", result });
    } catch (e) {
      setStage({ kind: "idle" });
      setError((e as Error)?.name === "AbortError" ? "Upload paused — you can resume it later." : errorMessage(e));
    } finally {
      abortRef.current = null;
      void releaseWakeLock();
    }
  };

  if (stage.kind === "done") {
    return (
      <>
        <Result result={stage.result} burn={burn} onReset={reset} />
        <SentList />
        <HomeShowcase onSelectSku={(sku) => setCheckoutSku(sku)} />
        {checkoutSku && (
          <CryptoCheckoutModal
            defaultSku={checkoutSku}
            onSuccess={(code) => {
              setPassCode(code);
              setCheckoutSku(null);
            }}
            onClose={() => setCheckoutSku(null)}
          />
        )}
      </>
    );
  }

  const { t } = useUi();
  const busy = stage.kind === "busy";
  const total = files.reduce((s, f) => s + f.size, 0);
  const expiryOptions = [
    { label: t.exp1h, seconds: 3600 },
    { label: t.exp6h, seconds: 6 * 3600 },
    { label: t.exp24h, seconds: 24 * 3600 },
    { label: t.exp72h, seconds: 72 * 3600 },
    { label: t.exp7d, seconds: 7 * 86_400 },
    { label: t.exp30d, seconds: 30 * 86_400 },
    { label: t.exp1y, seconds: 365 * 86_400 },
  ];

  return (
    <>
      <section className="hero-split">
        {/* Left Column: 4-Tab Auto-Rotating Story Carousel */}
        <HeroStoryLeft onSelectSku={(sku) => setCheckoutSku(sku)} />

        {/* Right Column: Sticky Compact Transfer Widget Card (SendGB-killer) */}
        <div
          className={`transfer-widget-card${dragging ? " glow" : ""}`}
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (!busy) addFiles(e.dataTransfer.files);
          }}
        >
          {/* Top 2 Tabs */}
          <div className="widget-top-tabs">
            <Link to="/drop" className="widget-top-tab primary">
              <Zap size={14} /> {t.limeDropP2p}
            </Link>
            <button type="button" className="widget-top-tab dark" onClick={() => setCheckoutSku("exceed-1tb")}>
              <UploadCloud size={14} /> {t.send1Tb}
            </button>
          </div>

          <div className="widget-body">
            {!busy && <ResumeBanner onResume={resume} />}

            {/* Add File & Add Folder Side-by-Side Buttons */}
            <div className="widget-pick-btns">
              <button type="button" className="widget-pick-btn file" disabled={busy} onClick={() => inputRef.current?.click()}>
                <UploadCloud size={16} /> {t.addFile}
              </button>
              <button type="button" className="widget-pick-btn folder" disabled={busy} onClick={() => folderInputRef.current?.click()}>
                <FolderUp size={16} /> {t.addFolder}
              </button>
            </div>

            {/* Trust Line + Increase Limit */}
            <div className="widget-trust-line">
              <span><Check size={12} color="var(--lime)" /> {t.freeTag}</span>
              <span><Check size={12} color="var(--lime)" /> {t.noSignupTag}</span>
              <span><Check size={12} color="var(--lime)" /> {t.upTo5GbTag}</span>
            </div>
            <div style={{ textAlign: "center", marginTop: -4 }}>
              <button
                type="button"
                onClick={() => setCheckoutSku("exceed-200g")}
                style={{ background: "transparent", border: 0, color: "var(--lime)", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >
                {passCode ? `✓ Pass Active: ${passCode}` : t.increaseLimit}
              </button>
            </div>

            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              disabled={busy}
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={folderInputRef}
              type="file"
              // @ts-expect-error webkitdirectory is standard across modern browsers
              webkitdirectory=""
              multiple
              hidden
              disabled={busy}
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />

            {/* Selected Files List */}
            {files.length > 0 && (
              <>
                <ul className="files">
                  {files.map((f, i) => (
                    <li className="file" key={`${f.name}-${f.size}-${f.lastModified}`}>
                      <div className="file-info">
                        <FileIcon name={f.name} type={f.type} size={18} />
                        <span className="name" title={f.name}>{f.name}</span>
                        <span className="size">{formatBytes(f.size)}</span>
                      </div>
                      {!busy && (
                        <div className="file-actions">
                          {canOptimizeImage(f) && (
                            <button
                              type="button"
                              className="btn ghost btn-sm"
                              style={{ padding: "3px 7px", fontSize: 11, color: "var(--lime)" }}
                              title="Compress image to WebP in browser before sending"
                              onClick={async () => {
                                try {
                                  const opt = await optimizeImageInBrowser(f);
                                  setFiles((prev) => prev.map((x, j) => (j === i ? opt : x)));
                                } catch {
                                  /* ignore */
                                }
                              }}
                            >
                              ⚡ WebP
                            </button>
                          )}
                          <button
                            className="btn ghost btn-sm btn-icon"
                            aria-label={`Remove ${f.name}`}
                            onClick={() => setFiles(files.filter((_, j) => j !== i))}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="hint" style={{ fontSize: 11, textAlign: "right" }}>
                  {`${files.length} file(s) · ${formatBytes(total)}`}
                </div>
              </>
            )}

            {/* Form Inputs Inside Widget (SendGB-Style) */}
            <div className="widget-field">
              <textarea
                rows={3}
                maxLength={2000}
                disabled={busy}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t.messagePlaceholder}
              />
              <small className="hint" style={{ position: "absolute", right: 10, bottom: 8, fontSize: 10 }}>
                {message.length}/2000
              </small>
            </div>

            <div className="widget-pw-expiry-row">
              <div className="widget-field">
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder={t.passwordPlaceholder}
                  value={password}
                  disabled={busy}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setUsePassword(e.target.value.length > 0);
                  }}
                />
              </div>
              <div className="widget-field">
                <select value={retention} disabled={busy} onChange={(e) => setRetention(Number(e.target.value))}>
                  {expiryOptions.map((o) => (
                    <option key={o.seconds} value={o.seconds}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <label className="check" style={{ fontSize: 12 }}>
                <input type="checkbox" checked={burn} disabled={busy} onChange={(e) => setBurn(e.target.checked)} />
                <Flame size={13} color="var(--warn)" />
                {t.burnAfterDownload}
              </label>
              <button
                type="button"
                className="btn ghost btn-sm"
                style={{ padding: "4px 8px", fontSize: 11 }}
                onClick={() => setShowOptions(!showOptions)}
              >
                {showOptions ? "Hide Pro Options" : "Branding & Paywall ▾"}
              </button>
            </div>

            {showOptions && (
              <div className="options" style={{ marginTop: 4, padding: 10, gap: 8 }}>
                <div className="widget-field">
                  <input
                    placeholder="Studio / Brand Name (Verified Delivery badge)"
                    maxLength={64}
                    disabled={busy}
                    value={senderBrand}
                    onChange={(e) => setSenderBrand(e.target.value)}
                  />
                </div>
                <div className="widget-field">
                  <input
                    type="number"
                    min={2}
                    max={10000}
                    step="0.01"
                    placeholder="Pay-to-Decrypt Price in $ USD (optional, min $2)"
                    disabled={busy}
                    value={paywallUsd}
                    onChange={(e) => setPaywallUsd(e.target.value)}
                  />
                </div>
              </div>
            )}

            {error && <div className="alert error" style={{ marginTop: 4 }}>{error}</div>}

            {busy ? (
              <Progress progress={stage.progress} stats={stage.stats} onCancel={() => abortRef.current?.abort()} />
            ) : (
              <div className="widget-bottom-bar">
                <div className="widget-mode-switch" title="Zero-Knowledge Link & Burn Controls">
                  <button
                    type="button"
                    className={`widget-mode-btn${!burn ? " active" : ""}`}
                    onClick={() => setBurn(false)}
                    title="Standard Link Mode"
                  >
                    <Lock size={15} />
                  </button>
                  <button
                    type="button"
                    className={`widget-mode-btn${burn ? " active" : ""}`}
                    onClick={() => setBurn(true)}
                    title="Burn on 1st Download"
                  >
                    <Flame size={15} />
                  </button>
                </div>
                <button className="widget-send-btn" disabled={files.length === 0} onClick={start}>
                  {t.sendBtn}
                </button>
              </div>
            )}

            <div ref={turnstileRef} />
          </div>
        </div>
      </section>

      <SentList />

      <HomeShowcase onSelectSku={(sku) => setCheckoutSku(sku)} />

      {checkoutSku && (
        <CryptoCheckoutModal
          defaultSku={checkoutSku}
          onSuccess={(code) => {
            setPassCode(code);
            setCheckoutSku(null);
          }}
          onClose={() => setCheckoutSku(null)}
        />
      )}
    </>
  );
}

function Progress({ progress, stats, onCancel }: { progress: UploadProgress | null; stats: TransferStats | null; onCancel: () => void }) {
  const pct = stats?.percent ?? (progress && progress.bytesTotal > 0 ? Math.min(100, Math.round((progress.bytesDone / progress.bytesTotal) * 100)) : 0);
  const label =
    progress === null
      ? "Preparing crypto session…"
      : progress.phase === "preparing"
        ? "Requesting storage pipeline…"
        : progress.phase === "finalizing"
          ? "Verifying container with storage…"
          : `Encrypting & uploading ${progress.fileName}`;

  return (
    <div className="progress" aria-live="polite">
      <div className="bar">
        <div style={{ width: `${pct}%` }} />
      </div>
      <div className="meta">
        <span>{label}</span>
        <div className="progress-stats">
          {stats?.speedFormatted && <span>{stats.speedFormatted}</span>}
          {stats?.etaFormatted && stats.etaFormatted !== "—" && <span>ETA: {stats.etaFormatted}</span>}
          <span>{pct}%</span>
        </div>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn danger btn-sm" onClick={onCancel}>
          Cancel upload
        </button>
      </div>
    </div>
  );
}

function Result({ result, burn, onReset }: { result: UploadResult; burn: boolean; onReset: () => void }) {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [extendModal, setExtendModal] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(result.expiresAt);

  useEffect(() => {
    if (!showQr || qr) return;
    import("qrcode")
      .then((m) => m.toDataURL(result.link, { margin: 1, width: 240, color: { dark: "#f0f6f0", light: "#0d140e" } }))
      .then(setQr)
      .catch(() => setQr(null));
  }, [showQr, qr, result.link]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* input selectable */
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Encrypted transfer via LimeShare", url: result.link });
      } catch {
        /* user dismissed */
      }
    } else {
      await copy();
    }
  };

  const telegramShare = `https://t.me/share/url?url=${encodeURIComponent(result.link)}&text=${encodeURIComponent("Encrypted file transfer via LimeShare")}`;
  const whatsappShare = `https://api.whatsapp.com/send?text=${encodeURIComponent("Encrypted files: " + result.link)}`;

  return (
    <section className="panel result glow">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(0,255,102,0.15)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00ff66" }}>
          <Lock size={18} />
        </div>
        <div>
          <h2>Transfer sealed &amp; ready</h2>
          <p className="hint" style={{ margin: 0 }}>
            Encrypted with AES-256-GCM. The key lives after <code>#</code> and never reaches the server.
          </p>
        </div>
      </div>

      <div className="linkbox">
        <input readOnly value={result.link} onFocus={(e) => e.currentTarget.select()} />
        <button className="btn" onClick={copy}>
          {copied ? <Check size={16} /> : <Copy size={16} />}
          <span>{copied ? "Copied!" : "Copy link"}</span>
        </button>
      </div>

      <div className="share-strip">
        <button className="btn ghost" onClick={shareNative}>
          <Share2 size={14} /> Share
        </button>
        <a className="btn ghost" href={telegramShare} target="_blank" rel="noopener noreferrer">
          Telegram
        </a>
        <a className="btn ghost" href={whatsappShare} target="_blank" rel="noopener noreferrer">
          WhatsApp
        </a>
        <button className="btn ghost" onClick={() => setShowQr(!showQr)}>
          <QrCode size={14} /> {showQr ? "Hide QR" : "QR Code"}
        </button>
      </div>

      {showQr && qr && (
        <div style={{ textAlign: "center", margin: "16px 0", padding: 12, background: "rgba(0,0,0,0.5)", borderRadius: 12, display: "inline-block" }}>
          <img src={qr} alt="QR code" width={220} height={220} style={{ borderRadius: 8, display: "block" }} />
          <small className="hint" style={{ marginTop: 6, display: "block" }}>Scan with mobile camera to decrypt</small>
        </div>
      )}

      <div className="kv">
        <span>Retention</span>
        <b>{expiresAt ? new Date(expiresAt).toLocaleString() : "Active subscription"}</b>
        <span>Burn-on-read</span>
        <b>{burn ? "Yes — transfer is destroyed upon first recipient pull" : "No"}</b>
        <span>Password</span>
        <b>{result.protected ? "Protected with Argon2id (send password separately)" : "None"}</b>
      </div>

      {/* SendGB-killer Post-Upload KeepAlive Extend */}
      <div style={{ marginTop: 18, padding: "14px 16px", borderRadius: 12, background: "rgba(0,255,102,0.06)", border: "1px solid rgba(0,255,102,0.25)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <b style={{ color: "var(--lime)", fontSize: 14, display: "block" }}>Would you like to keep this transfer online for 1 Year?</b>
          <span className="hint" style={{ fontSize: 12 }}>Extend storage duration up to 365 days with unlimited downloads — no signup needed.</span>
        </div>
        <button className="btn btn-sm" onClick={() => setExtendModal(true)}>
          <Zap size={14} /> Extend to 1 Year ($1.99)
        </button>
      </div>

      <div className="row" style={{ marginTop: 22 }}>
        <button className="btn ghost" onClick={onReset}>
          <RotateCcw size={14} /> Send more files
        </button>
      </div>

      {extendModal && (
        <CryptoCheckoutModal
          defaultSku="extend-1y"
          allowedSkus={["extend-6m", "extend-1y", "vault-pass-15"]}
          transferId={result.transferId}
          title="KeepAlive Extend — Keep Link Online Up to 1 Year"
          onSuccess={(code) => {
            api.extend(result.transferId, { passCode: code })
              .then((r) => setExpiresAt(r.expiresAt))
              .catch(() => setExpiresAt(Date.now() + 365 * 86_400_000));
            setExtendModal(false);
          }}
          onClose={() => setExtendModal(false)}
        />
      )}
    </section>
  );
}

function ResumeBanner({ onResume }: { onResume: (record: ResumeRecord, files: File[]) => void }) {
  const [records, setRecords] = useState<ResumeRecord[]>([]);
  const [picked, setPicked] = useState<Record<string, File[]>>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    void listResumable().then(setRecords);
  }, []);

  if (records.length === 0) return null;

  const discard = async (r: ResumeRecord) => {
    await discardResume(r);
    setRecords((prev) => prev.filter((x) => x.transferId !== r.transferId));
  };

  return (
    <div className="alert warn" style={{ marginTop: 0, marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <RotateCcw size={16} />
        <b>Unfinished upload session detected</b>
      </div>
      {records.map((r) => {
        const p = resumeProgress(r);
        const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
        const chosen = picked[r.transferId] ?? [];
        return (
          <div key={r.transferId} style={{ marginTop: 10, padding: 10, background: "rgba(0,0,0,0.3)", borderRadius: 8 }}>
            <div style={{ fontSize: 13 }}>
              {r.files.map((f) => f.name).join(", ")} · {formatBytes(p.total)} · {pct}% completed · window closes in{" "}
              {formatDuration(r.draftExpiresAt - Date.now())}
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <input
                ref={(el) => {
                  inputRefs.current[r.transferId] = el;
                }}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const list = Array.from(e.target.files ?? []);
                  setPicked((prev) => ({ ...prev, [r.transferId]: [...(prev[r.transferId] ?? []), ...list] }));
                  e.target.value = "";
                }}
              />
              <button className="btn ghost btn-sm" onClick={() => inputRefs.current[r.transferId]?.click()}>
                {chosen.length ? `Selected ${chosen.length}/${r.files.length}` : "Re-select file(s)"}
              </button>
              <button className="btn btn-sm" disabled={chosen.length === 0} onClick={() => onResume(r, chosen)}>
                Resume upload
              </button>
              <button className="btn danger btn-sm" onClick={() => discard(r)}>
                Discard
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

type Status = { downloads: number; state: string } | { error: string };

function SentList() {
  const [items, setItems] = useState<SentTransfer[]>([]);
  const [statuses, setStatuses] = useState<Record<string, Status>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    setItems(pruneSent());
  }, []);

  useEffect(() => {
    const recent = items.slice(0, 10);
    recent.forEach((t) => {
      api
        .status(t.transferId, t.token)
        .then((s) => setStatuses((p) => ({ ...p, [t.transferId]: { downloads: s.downloadCount, state: s.state } })))
        .catch((e) => setStatuses((p) => ({ ...p, [t.transferId]: { error: errorMessage(e) } })));
    });
  }, [items.length]);

  if (items.length === 0) return null;

  const destroy = async (t: SentTransfer) => {
    if (!confirm(`Permanently destroy this transfer right now? Recipients will immediately get 410 Gone.`)) return;
    setBusyId(t.transferId);
    try {
      const res = await api.remove(t.transferId, t.token);
      markDestroyed(t.transferId, res.receipt);
      setItems(loadSent());
    } catch (e) {
      alert(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const forget = (t: SentTransfer) => {
    removeSent(t.transferId);
    setItems(loadSent());
  };

  return (
    <section className="panel" style={{ marginTop: 22 }}>
      <h3 style={{ margin: "0 0 12px", fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
        <Clock size={16} /> Transfers sent from this browser
      </h3>
      <ul className="files">
        {items.map((t) => {
          const s = statuses[t.transferId];
          const dead = s && "state" in s && ["deleted", "failed", "blocked", "trashed"].includes(s.state);
          const expired = t.expiresAt !== null && t.expiresAt < Date.now();
          return (
            <li className="file" key={t.transferId} style={{ flexWrap: "wrap" }}>
              <div className="file-info">
                <FileIcon name={t.files[0]?.name ?? "file"} size={16} />
                <span className="name" title={t.files.map((f) => f.name).join(", ")}>
                  {t.files.map((f) => f.name).join(", ")}
                </span>
                <span className="size">
                  {dead || expired
                    ? "Purged"
                    : s && "downloads" in s
                      ? `${s.downloads} pull${s.downloads === 1 ? "" : "s"} · ${t.expiresAt ? formatDuration(t.expiresAt - Date.now()) + " left" : ""}`
                      : "…"}
                </span>
              </div>
              <div className="file-actions">
                {!dead && !expired && !t.destroyed ? (
                  <>
                    <button className="btn ghost btn-sm" onClick={() => navigator.clipboard.writeText(t.link)}>
                      <Copy size={12} /> Copy
                    </button>
                    <button className="btn danger btn-sm" disabled={busyId === t.transferId} onClick={() => destroy(t)}>
                      <Trash2 size={12} /> Destroy
                    </button>
                  </>
                ) : (
                  <>
                    {t.receipt?.signature && (
                      <Link
                        to={`/verify-receipt?id=${encodeURIComponent(t.transferId)}&sig=${encodeURIComponent(t.receipt.signature)}&time=${t.receipt.deletedAt}&files=${t.receipt.fileCount}&bytes=${t.receipt.totalBytes}`}
                        className="btn ghost btn-sm"
                        style={{ color: "var(--lime)", border: "1px solid rgba(0,255,102,0.3)" }}
                      >
                        <ShieldCheck size={12} /> View Certificate
                      </Link>
                    )}
                    <button className="btn ghost btn-sm" onClick={() => forget(t)}>
                      Dismiss
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
